import { Typography, Box, Divider, Chip, IconButton, Tooltip } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { Funeral, Contract } from '../types';

import {
    Datagrid,
    DateField,
    ListContextProvider,
    ResourceContextProvider,
    TextField,
    useGetMany,
    useGetManyReference,
    useList,
    useRecordContext,
    useTranslate,
    useNotify,
    ReferenceField,
    ReferenceManyField,
    SingleFieldList,
    ChipField,
    BooleanField,
    RaRecord,
    NumberField,
    TextFieldProps,

  } from 'react-admin';
import { PriceField } from '../components/PriceField';
import { InvoiceBalanceField } from '../components/InvoiceBalanceField';
import ConvertPreNeedButton from '../contracts/ConvertPreNeedButton';

const contractRowStyle = (record: any, index: number) => ({
  backgroundColor: record.is_preneed ? '#E1C1F7' : 
    record.legacy_lines?.lines?.length > 0 ? '#fef5e4' : 'white',
});

const CopyContractLinkButton = ({ funeralUuid }: { funeralUuid?: string }) => {
  const contract = useRecordContext<Contract>();
  const notify = useNotify();

  if (!contract || !funeralUuid) return null;

  const handleCopy = async (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent row click navigation
    const fallback = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
      ? 'http://localhost:3010' : 'https://cleocremation.com';
    const auraAppUrl = (process.env.REACT_APP_AURA_APP_URL || fallback).replace(/\/$/, '');
    const url = `${auraAppUrl}/app/${funeralUuid}/contract-start?contractId=${contract.id}`;
    try {
      await navigator.clipboard.writeText(url);
      notify('Contract link copied to clipboard', { type: 'success' });
    } catch {
      notify('Failed to copy link', { type: 'error' });
    }
  };

  return (
    <Tooltip title="Copy signing link">
      <IconButton size="small" onClick={handleCopy}>
        <ContentCopyIcon fontSize="small" />
      </IconButton>
    </Tooltip>
  );
};

const FuneralLinkedContractsList = () => {
  const translate = useTranslate();
  const record = useRecordContext<Funeral>();

  const { data: supabaseContracts, isLoading: isContractsLoading } = useGetManyReference(
    'contracts',
    { target: 'funeral_id', id: record?.id },
    { enabled: !!record }
  );

  const contracts = supabaseContracts ? supabaseContracts : [];
  const hasAtNeed = contracts.some((contract) => !contract.is_preneed);


  const listContext = useList({ data: contracts });
  return (
    <Box my={4}>
      <Typography variant='h6'>
        {translate('resources.contracts.name', { smart_count: contracts.length })}
      <Divider />
      </Typography>
        { contracts.length == 0 ?
          <Typography mt={1}>No contracts</Typography>
          :
        <ResourceContextProvider value="contracts">
            <ListContextProvider value={listContext}>
                <Datagrid rowClick="show" bulkActionButtons={false} hover={true} rowStyle={contractRowStyle}>
                    <TextField source="id" />
                    <DateField source="issue_date" />
                    {/* <ReferenceField source="client_id" reference="clients">
                        <TextField source="name" />
                    </ReferenceField> */}
                    <TotalAmountField label="Total Amount"/>
                    <ReferenceManyField
                      label="Invoice"
                      target="contract_id"
                      reference="sb_invoices"
                    >
                      <SingleFieldList linkType={false}>
                        <ChipField source="id" size='small' sx={{ backgroundColor: '#BFE1D5', color: 'black'}}/>
                      </SingleFieldList>
                    </ReferenceManyField>
                    <ReferenceManyField
                      label="Credit"
                      target="contract_id"
                      reference="sb_creditmemos"
                    >
                      <SingleFieldList linkType={false}>
                        <ChipField source="id" size='small' sx={{ backgroundColor: '#F9B7B5', color: 'black'}} />
                      </SingleFieldList>
                    </ReferenceManyField>
                    <InvoiceBalanceField label="Balance"/>
                    <BooleanField source="is_preneed" label="Pre-Need" />
                    <PreneedFulfillmentChip />
                    <ConvertPreNeedButton hasAtNeed={hasAtNeed} />
                    <CopyContractLinkButton funeralUuid={record?.uuid} />
            </Datagrid>
            </ListContextProvider>
        </ResourceContextProvider>
    }
    </Box>
  );
};

const PreneedFulfillmentChip = () => {
  const record = useRecordContext<Contract>();

  if (!record?.is_preneed) {
    return null;
  }

  const status = record.fulfillment_status || 'pending';
  const label =
    status === 'fulfilled'
      ? 'Fulfilled'
      : status === 'cancelled'
        ? 'Cancelled'
        : 'Pending';

  const color =
    status === 'fulfilled'
      ? 'success'
      : status === 'cancelled'
        ? 'error'
        : 'warning';

  return <Chip label={label} color={color} size="small" />;
};

const TotalAmountField = (props: TextFieldProps) => {

  const record = useRecordContext();

  if(record?.legacy_lines?.lines?.length > 0){

    let total = 0;

    record.legacy_lines?.lines?.map((line: any) => {
      if(line['Type'] != "0"){
        total += parseInt(line['Amount']);
      }
    });

    if(record.is_preneed && record.legacy_lines?.lines?.length > 0){
      total = parseFloat(record.legacy_lines['Total']) * 100;
    }

    return(
      <Typography>${total/100}</Typography>
    );
  }
  else{
    return(
      <PriceField source="total_amount" />
    );
  }
}


export default FuneralLinkedContractsList;
